
/**
 * Site main navigation module
 *
 * data-nav-header is optional, it is used for intersection observer. It should be an element that is not sticky.
 * Expects the following markup, where ELEMENT is the constructor element:

  <header data-nav-header>
    <ELEMENT>
      <button data-nav-action-trigger aria-controls="search">I open a search</button>
      <div id="search">I am the search</div>

      <button data-action-trigger aria-controls="mobile-nav">I open a mobile nav</button>
      <div id="mobile-nav">I am the mobile nav</div>

      <ul>
        <li>
          <button data-nav-menu-trigger aria-controls="menu-1">I open a submenu, sibling menus stay open</button>
          <ul id="menu-1">I am the submenu</ul>
        </li>
      </ul>

      <ul>
        <li>
          <button data-nav-desktop-menu-trigger aria-controls="menu-1">I open a submenu, and close sibling menus</button>
          <ul id="menu-1">I am the submenu</ul>
        </li>
      </ul>
    </ELEMENT>
  </header>

 */

type NavigationSettings = {
  /** Classname for navigation bar changes on scroll */
  intersectingClassName: string;
  /** Classname for open action */
  actionOpenClassName: string;
};

class Navigation {
  #settings: NavigationSettings;
  #el: HTMLElement;
  #actionTriggers: NodeListOf<HTMLElement>;
  #mobileMenuTriggers: NodeListOf<HTMLElement>;
  #desktopMenuTriggers: Array<HTMLElement>;
  #heightWatchElements: NodeListOf<HTMLElement>;
  #headerObserver: IntersectionObserver | null = null;

  constructor(element: HTMLElement, options: Partial<NavigationSettings> = {}) {
    const defaults = {
      intersectingClassName: 'navigation--intersecting',
      actionOpenClassName: 'open',
    };
    const settings = Object.assign({}, defaults, options);

    this.#el = element;
    this.#settings = settings;
    this.#actionTriggers = this.#el.querySelectorAll('[data-nav-action-trigger]');
    this.#mobileMenuTriggers = this.#el.querySelectorAll('[data-nav-menu-trigger]');
    this.#desktopMenuTriggers = Array.from(this.#el.querySelectorAll('[data-nav-desktop-menu-trigger]'));
    this.#heightWatchElements = this.#el.querySelectorAll('[data-nav-height-watch]');

    this.#initListeners();
    console.log('navigation.init()');
  }

  #initListeners() {
    this.#actionTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.onActionTriggerClick.bind(this));

      if(typeof trigger.dataset.navActionFocustrap === 'string') {
        const actionTarget = document.getElementById(trigger.getAttribute('aria-controls') || '');
        if(actionTarget) {
          actionTarget.addEventListener('keydown', this.onActionKeyDown.bind(this));
        }
      }
    });

    this.#mobileMenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.onMobileMenuTriggerClick.bind(this));
    });

    this.#desktopMenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.onDesktopMenuTriggerClick.bind(this));
    });

    document.addEventListener('click', this.onOutsideClick.bind(this));

    document.addEventListener('keydown', this.onDocumentKeyDown.bind(this));

    if(this.#heightWatchElements.length) {
      const throttle = (cb, delay) => {
        let throttling = false;

        return (...args) => {
          if (!throttling) {
            throttling = true;

            cb(...args);

            setTimeout(() => {
              throttling = false;
            }, delay);
          }
        };
      };

      // Since nested menus are absolutely positioned, we need to update parent height on resize manully.
      window.addEventListener('resize', throttle(this.onResize.bind(this), 200));
    }

    // Change header class on scroll
    const header = document.querySelector('[data-nav-header]') as HTMLElement;
    if(header) {
      const navHeight = this.#el.getBoundingClientRect().height;
      this.#headerObserver = new IntersectionObserver(this.updateNavClass.bind(this), {
        root: null,
        threshold: 0,
        rootMargin: `${navHeight}px`,
      });
      this.#headerObserver.observe(header);
    }

    // Listen to quick search events
    // In this case close all actions when esc is pressed in quick search input
    document.addEventListener('quickSearch.escape', this.closeAllActions.bind(this));
  }

  updateNavClass(entries: IntersectionObserverEntry[]): void {
    const [entry] = entries;

    this.#el.classList.toggle(this.#settings.intersectingClassName, !entry.isIntersecting);
  }

  onActionTriggerClick(event: Event): void {
    const actionTrigger = event.currentTarget as HTMLElement;
    const actionTarget = document.getElementById(actionTrigger.getAttribute('aria-controls') || '');

    if(!actionTarget) {
      return;
    }

    if(actionTarget.classList.contains(this.#settings.actionOpenClassName)) {
      actionTarget.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('no-scroll');
    } else {
      this.closeAllActions();
      actionTarget.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('no-scroll');

      // Focus the first focusable element in the target
      if(typeof actionTrigger.dataset.navActionFocustrap === 'string') {
        const focusable = actionTarget.querySelector('button, a, input, select, textarea, .js-focusable') as HTMLElement;
        if(focusable) {
          focusable.focus();
        }
      }
    }
  }

  /**
   * Manual focus control for keyboard navigation
   * Closes action when focus is moved outside of the action
   */
  onActionKeyDown(event: KeyboardEvent): void {
    if(event.key !== 'Tab') {
      return;
    }

    const actionTarget = event.currentTarget as HTMLElement;
    const actionTrigger = document.querySelector(`[aria-controls="${actionTarget.id}"]`) as HTMLElement;
    const focusables = (Array.from(actionTarget.querySelectorAll('button, a, input, select, textarea')) as HTMLElement[]).filter(el => (el as any).checkVisibility()); //TS not recognizing checkVisibility
    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];
    const direction = event.shiftKey ? 'backwards' : 'forwards';

    if(document.activeElement === lastFocusable && direction === 'forwards') {
      // Focused past the last focusable element, should close the action and set focus back to next trigger
      this.closeAction(actionTarget, actionTrigger);
      actionTrigger.focus();
    } else if(document.activeElement === firstFocusable && direction === 'backwards') {
      // Focused before the first focusable element, should close the action and set focus back to prev trigger
      this.closeAction(actionTarget, actionTrigger);
      actionTrigger.focus();
    }

  }

  closeAction(target: HTMLElement, trigger: HTMLElement): void {
    target.classList.remove(this.#settings.actionOpenClassName);
    trigger.classList.remove(this.#settings.actionOpenClassName);
    trigger.setAttribute('aria-expanded', 'false');
  }

  closeAllActions(): void {
    this.#actionTriggers.forEach(trigger => {
      const target = document.getElementById(trigger.getAttribute('aria-controls') || '');
      if(target) {
        this.closeAction(target, trigger);
      }
    });
  }

  /**
   * In mobile menu accordion, opening one element should not close others.
   */
  onMobileMenuTriggerClick(event: Event): void {
    const actionTrigger = event.currentTarget as HTMLElement;
    const actionTarget = document.getElementById(actionTrigger.getAttribute('aria-controls') || '');

    if(!actionTarget) {
      return;
    }

    if(actionTarget.classList.contains(this.#settings.actionOpenClassName)) {
      actionTarget.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'false');
      this.updateTriggerLabel(actionTrigger, false);
    } else {
      actionTarget.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'true');
      this.updateTriggerLabel(actionTrigger, true);
    }
  }

  /**
   * In desktop menu, opening one element should close other triggers, but leave current trigger ancestors and descendants open.
   * It also should update height of the parent menu to match the height of the open submenu (because of position absolute)
   */
  onDesktopMenuTriggerClick(event: Event): void {
    const actionTrigger = event.currentTarget as HTMLElement;
    const actionTarget = document.getElementById(actionTrigger.getAttribute('aria-controls') || '');

    if(!actionTarget) {
      return;
    }

    if(actionTarget.classList.contains(this.#settings.actionOpenClassName)) {
      actionTarget.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.classList.remove(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'false');
      this.updateTriggerLabel(actionTrigger, false);
    } else {
      // Close others (not ancestors or descendants of current trigger)
      const openTriggers = this.#desktopMenuTriggers.filter(trigger => trigger.classList.contains(this.#settings.actionOpenClassName));
      const triggersToBeClosed = openTriggers.filter(trigger => {
        const target = document.getElementById(trigger.getAttribute('aria-controls') || '');

        if(target && target.contains(actionTrigger)) {
          return false;
        }

        return true;
      });

      triggersToBeClosed.forEach(trigger => {
        const target = document.getElementById(trigger.getAttribute('aria-controls') || '');
        if(target) {
          target.classList.remove(this.#settings.actionOpenClassName);
          trigger.classList.remove(this.#settings.actionOpenClassName);
          trigger.setAttribute('aria-expanded', 'false');
          this.updateTriggerLabel(trigger, false);
        }
      });

      //Open self
      actionTarget.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.classList.add(this.#settings.actionOpenClassName);
      actionTrigger.setAttribute('aria-expanded', 'true');
      this.updateTriggerLabel(actionTrigger, true);

      this.updateHeights();
    }
  }

  /**
  * Check for escape to close menu
  */
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    let activeTrigger;
    this.#actionTriggers.forEach(trigger => {
      const actionTarget = document.getElementById(trigger.getAttribute('aria-controls') || '');

      if (actionTarget?.classList.contains(this.#settings.actionOpenClassName)) {
        activeTrigger = trigger;
      }
    });

    this.closeAllActions();

    // Focus last used trigger
    if (activeTrigger) activeTrigger.focus();
  }

  updateTriggerLabel(trigger: HTMLElement, isOpen: boolean): void {
    if(!trigger) {
      return;
    }

    if(trigger.hasAttribute('aria-label') && trigger.dataset.labelOpen && trigger.dataset.labelClosed) {
      trigger.setAttribute('aria-label', isOpen ? trigger.dataset.labelOpen : trigger.dataset.labelClosed);
    }
  }

  updateHeights(): void {
    this.#heightWatchElements.forEach(element => {
      const listElementHeights = Array.from(element.querySelectorAll(`ul`)).map(el => el.getBoundingClientRect().height);
      const maxHeight = Math.max(...listElementHeights);
      if(maxHeight > 0) {
        element.style.height = `${maxHeight}px`;
      }
    });
  }

  onResize(): void {
    this.updateHeights();
  }

  onOutsideClick(event: Event): void {
    if(event.target instanceof HTMLElement && !this.#el.contains(event.target)) {
      this.closeAllActions();
    }
  }
}

export default Navigation;
